// src/pages/Perfil.js
import React, { useState } from 'react';
import { usePerfil } from '../hooks/usePerfil';
import { use2FA } from '../hooks/use2FA';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { getIniciales, nombreEspecialidad } from '../utils/formatos';

const ROLES_LABEL = { medico: 'Médico', supermedico: 'Supermédico', recepcion: 'Recepción', admin: 'Administrador' };

export default function Perfil() {
    // 🚀 SRP: Separación absoluta de responsabilidades
    const { user, formDatos, editando, setEditando, guardando, handleInputChange, guardarCambiosPerfil, reloadUser } = usePerfil();
    const { showToast } = useToast();
    const { logout } = useAuth();

    // Eliminamos 'secret' de la desestructuración para evitar el warning
    const { qrCodeUrl, token6Digitos, setToken6Digitos, procesando2FA, mostrarModalVerificacion, setMostrarModalVerificacion, iniciarConfiguracion2FA, verificarYActivar2FA, desactivar2FA } = use2FA();

    // 🛡️ FIX: el backend expone 'isTwoFactorEnabled' (antes se leían nombres
    // que no existían y el 2FA siempre aparecía como inactivo).
    const es2FAActivo = Boolean(user?.isTwoFactorEnabled);
    const [pwdDesactivar2FA, setPwdDesactivar2FA] = useState('');
    const [confirmandoBaja2FA, setConfirmandoBaja2FA] = useState(false);

    const handleDesactivar2FA = async () => {
        const ok = await desactivar2FA(pwdDesactivar2FA, reloadUser);
        if (ok) {
            setPwdDesactivar2FA('');
            setConfirmandoBaja2FA(false);
        }
    };
    const rol = user?.role || user?.rol || 'medico';

    // Cambio de contraseña
    const [pwd, setPwd] = useState({ actual: '', nueva: '', confirmar: '' });
    const [cambiandoPwd, setCambiandoPwd] = useState(false);

    const handleCambiarPassword = async () => {
        if (!pwd.actual || !pwd.nueva) { showToast('Completa la contraseña actual y la nueva.', 'error'); return; }
        if (pwd.nueva !== pwd.confirmar) { showToast('La nueva contraseña y su confirmación no coinciden.', 'error'); return; }
        try {
            setCambiandoPwd(true);
            await api.post('/auth/cambiar-password', { passwordActual: pwd.actual, passwordNueva: pwd.nueva });
            showToast('Contraseña actualizada. Vuelve a iniciar sesión por seguridad.', 'success');
            setTimeout(() => logout(), 1500); // el backend revoca todas las sesiones
        } catch (err) {
            showToast(err.message || 'No se pudo cambiar la contraseña.', 'error');
        } finally {
            setCambiandoPwd(false);
        }
    };

    const pwdInput = "w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:border-clinica-primary focus:ring-2 focus:ring-clinica-primary/15 transition-colors";

    return (
        <div className="p-6 md:p-8 space-y-8 max-w-4xl mx-auto animate-fade-in">
            {/* Cabecera con avatar */}
            <div className="flex items-center gap-4">
                <div className="flex items-center justify-center w-16 h-16 rounded-3xl bg-slate-900 text-white text-xl font-black shrink-0">
                    {getIniciales(user?.nombre, user?.apellidos)}
                </div>
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight">{user?.nombre} {user?.apellidos}</h1>
                    <p className="text-sm font-semibold text-slate-500 mt-0.5">
                        {ROLES_LABEL[rol] || rol} · {nombreEspecialidad(user?.especialidad) || 'General'}
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">

                {/* COLUMNA 1 & 2: FORMULARIO DE IDENTIDAD */}
                <div className="md:col-span-2 bg-white border border-slate-200 rounded-[2rem] p-6 shadow-sm space-y-6">
                    <div className="flex justify-between items-center border-b border-slate-100 pb-4">
                        <h2 className="text-lg font-black text-slate-800">Mis datos</h2>
                        <button
                            type="button"
                            onClick={() => editando ? guardarCambiosPerfil() : setEditando(true)}
                            className={`px-4 py-2 text-xs font-black uppercase tracking-wider rounded-xl transition-all ${editando ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                            disabled={guardando}
                        >
                            {guardando ? 'Guardando...' : editando ? 'Guardar Cambios' : 'Modificar'}
                        </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Añadidos htmlFor e id para cumplir con la accesibilidad estricta */}
                        <div>
                            <label htmlFor="input-nombre" className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Nombre</label>
                            <input id="input-nombre" type="text" name="nombre" value={formDatos.nombre} onChange={handleInputChange} disabled={!editando} className="w-full p-3 bg-slate-50 border border-slate-200 disabled:opacity-70 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:border-blue-500 transition-colors" />
                        </div>
                        <div>
                            <label htmlFor="input-apellidos" className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Apellidos</label>
                            <input id="input-apellidos" type="text" name="apellidos" value={formDatos.apellidos} onChange={handleInputChange} disabled={!editando} className="w-full p-3 bg-slate-50 border border-slate-200 disabled:opacity-70 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:border-blue-500 transition-colors" />
                        </div>
                        <div>
                            <label htmlFor="input-colegiado" className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Nº Colegiado</label>
                            <input id="input-colegiado" type="text" name="numeroColegiado" value={formDatos.numeroColegiado} onChange={handleInputChange} disabled title="Lo cambia el soporte de Klyndu" className="w-full p-3 bg-slate-50 border border-slate-200 disabled:opacity-70 rounded-xl text-sm font-mono font-bold text-slate-800 focus:outline-none focus:border-blue-500 transition-colors" />
                        </div>
                        <div>
                            <label htmlFor="input-especialidad" className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Especialidad</label>
                            <input id="input-especialidad" type="text" name="especialidad" value={nombreEspecialidad(formDatos.especialidad)} onChange={handleInputChange} disabled title="Lo cambia el soporte de Klyndu" className="w-full p-3 bg-slate-50 border border-slate-200 disabled:opacity-70 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:border-blue-500 transition-colors" />
                        </div>
                    </div>
                </div>

                {/* COLUMNA 3: ESCUDO DE DOBLE FACTOR (2FA) */}
                <div className="bg-white border border-slate-200 rounded-[2rem] p-6 shadow-sm flex flex-col justify-between min-h-[250px]">
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-black text-slate-800">Verificación en dos pasos</h2>
                            <span className={`w-2.5 h-2.5 rounded-full ${es2FAActivo ? 'bg-emerald-500 animate-pulse' : 'bg-rose-400'}`}></span>
                        </div>
                        <p className="text-xs text-slate-400 font-semibold leading-relaxed">
                            Además de la contraseña, pide un código del móvil al entrar. Protege las historias de los pacientes aunque alguien conozca tu contraseña.
                        </p>
                    </div>

                    <div className="pt-6">
                        {es2FAActivo ? (
                            confirmandoBaja2FA ? (
                                <div className="space-y-2">
                                    <label htmlFor="pwd-baja-2fa" className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Confirma tu contraseña</label>
                                    <input
                                        id="pwd-baja-2fa"
                                        type="password"
                                        autoComplete="current-password"
                                        value={pwdDesactivar2FA}
                                        onChange={(e) => setPwdDesactivar2FA(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleDesactivar2FA(); }}
                                        className={pwdInput}
                                    />
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => { setConfirmandoBaja2FA(false); setPwdDesactivar2FA(''); }}
                                            className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 font-black text-xs uppercase tracking-widest rounded-xl transition-all"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleDesactivar2FA}
                                            disabled={procesando2FA}
                                            className="flex-1 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-black text-xs uppercase tracking-widest rounded-xl transition-all disabled:opacity-60"
                                        >
                                            {procesando2FA ? 'Desactivando…' : 'Desactivar'}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setConfirmandoBaja2FA(true)}
                                    disabled={procesando2FA}
                                    className="w-full py-3 bg-rose-50 hover:bg-rose-600 border border-rose-100 text-rose-600 hover:text-white font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-sm"
                                >
                                    Desactivar Escudo 2FA
                                </button>
                            )
                        ) : (
                            <button
                                type="button"
                                onClick={iniciarConfiguracion2FA}
                                disabled={procesando2FA}
                                className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-slate-900/10"
                            >
                                {procesando2FA ? 'Iniciando...' : 'Activar Doble Factor'}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* ============ CAMBIO DE CONTRASEÑA ============ */}
            <div className="bg-white border border-slate-200 rounded-[2rem] p-6 shadow-sm space-y-5">
                <div className="border-b border-slate-100 pb-4">
                    <h2 className="text-lg font-black text-slate-800">Cambiar contraseña</h2>
                    <p className="text-xs font-semibold text-slate-400 mt-1">Mínimo 8 caracteres con al menos una letra y un número. Al cambiarla se cerrarán todas tus sesiones.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                        <label htmlFor="pwd-actual" className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Contraseña actual</label>
                        <input id="pwd-actual" type="password" autoComplete="current-password" value={pwd.actual} onChange={(e) => setPwd(p => ({ ...p, actual: e.target.value }))} className={pwdInput} />
                    </div>
                    <div>
                        <label htmlFor="pwd-nueva" className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Nueva contraseña</label>
                        <input id="pwd-nueva" type="password" autoComplete="new-password" value={pwd.nueva} onChange={(e) => setPwd(p => ({ ...p, nueva: e.target.value }))} className={pwdInput} />
                    </div>
                    <div>
                        <label htmlFor="pwd-confirmar" className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Repetir nueva</label>
                        <input id="pwd-confirmar" type="password" autoComplete="new-password" value={pwd.confirmar} onChange={(e) => setPwd(p => ({ ...p, confirmar: e.target.value }))} className={pwdInput} />
                    </div>
                </div>
                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={handleCambiarPassword}
                        disabled={cambiandoPwd}
                        className="px-6 py-2.5 text-sm font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-xl transition-colors shadow-md disabled:opacity-60"
                    >
                        {cambiandoPwd ? 'Actualizando…' : 'Actualizar contraseña'}
                    </button>
                </div>
            </div>

            {/* =========================================================
                📦 MODAL FLUIDO DE VERIFICACIÓN CRIPTOGRÁFICA (2FA)
               ========================================================= */}
            {mostrarModalVerificacion && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-fade-in" style={{ margin: 0 }}>
                    <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-md w-full p-8 border border-slate-200 text-center space-y-6">
                        <div>
                            <h3 className="text-xl font-black text-slate-900">Enlazar Authenticator</h3>
                            <p className="text-xs font-semibold text-slate-400 mt-1">Escanee el código QR oficial con Google Authenticator o Authy</p>
                        </div>

                        {/* Código QR Dinámico en Base64 */}
                        {qrCodeUrl && (
                            <div className="bg-slate-50 p-4 rounded-3xl inline-block border border-slate-100 shadow-inner">
                                <img src={qrCodeUrl} alt="Código QR de Vinculación 2FA" className="w-44 h-44 mx-auto select-none rounded-xl" />
                            </div>
                        )}

                        <div className="space-y-2 flex flex-col items-center">
                            <label htmlFor="input-2fa-token" className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Código de Verificación</label>
                            <input
                                id="input-2fa-token"
                                type="text"
                                maxLength={6}
                                placeholder="000000"
                                value={token6Digitos}
                                onChange={(e) => setToken6Digitos(e.target.value.replace(/\D/g, ''))}
                                className="w-40 text-center tracking-[0.5em] font-mono text-xl font-black p-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500"
                            />
                        </div>

                        <div className="flex gap-3 pt-2">
                            <button
                                type="button"
                                onClick={() => setMostrarModalVerificacion(false)}
                                className="flex-1 py-3 text-xs font-black uppercase tracking-wider text-slate-500 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={() => verificarYActivar2FA(reloadUser)}
                                disabled={procesando2FA}
                                className="flex-1 py-3 text-xs font-black uppercase tracking-wider text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-colors shadow-md"
                            >
                                {procesando2FA ? 'Validando...' : 'Confirmar Enlace'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}